/**
 * useGoldieStage
 * --------------------------------------------------------------------------
 * Returns the current Goldie Drop stage (0-6) based on the user's local
 * date. The drop runs from Mon May 11, 2026 through Sat May 16, 2026.
 * Sun May 17 onwards it auto-rolls back to 0 (theme off).
 *
 * Stage map:
 *   1 — Mon 5/11  "The Lights Go Out"   (black bg + glowing heading)
 *   2 — Tue 5/12  "Glitter Storm"       (+ falling sparkles)
 *   3 — Wed 5/13  "The Sunburst"        (+ rotating gold rays)
 *   4 — Thu 5/14  "Goldie Arrives"      (+ corner mascot)
 *   5 — Fri 5/15  "The Ticket Drops"    (+ floater + tile glow)
 *   6 — Sat 5/16  "BOOM"                (+ Dumpling intro + big hero)
 *
 * Manual overrides:
 *   - URL `?goldieDay=N` (0..6) — preview/demo any stage instantly. Useful
 *     for testing before 5/11 or showing a screenshot to leadership.
 *   - Theme background hex `#0A0600` — legacy "force Day 6" trigger kept
 *     for backward-compat with existing settings.
 *
 * Re-evaluates every 60s so a midnight rollover during an open session
 * still picks up the new stage without a hard refresh.
 */
import { useEffect, useState } from "react";

const SCHEDULE = {
  // Local-time calendar: year=2026, month=4 (May, 0-indexed), day → stage.
  // String keys to dodge JS's numeric-separator interpretation of `2026_4_11`.
  "2026-4-11": 1,
  "2026-4-12": 2,
  "2026-4-13": 3,
  "2026-4-14": 4,
  "2026-4-15": 5,
  "2026-4-16": 6,
};

function calcStage(now, themeBg) {
  // URL override takes precedence so we can demo any stage immediately.
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("goldieDay");
    if (raw !== null) {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n) && n >= 0 && n <= 6) return n;
    }
  } catch { /* SSR / private mode */ }

  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const key = `${y}-${m}-${d}`;
  const dateStage = SCHEDULE[key] || 0;
  if (dateStage > 0) return dateStage;

  // Legacy fallback — manual theme preset acts as "force Day 6" so admins
  // can preview the full effect any time of year.
  if ((themeBg || "").toUpperCase() === "#0A0600") return 6;

  return 0;
}

export default function useGoldieStage(themeBg) {
  const [stage, setStage] = useState(() => calcStage(new Date(), themeBg));

  useEffect(() => {
    setStage(calcStage(new Date(), themeBg));
    const id = setInterval(() => setStage(calcStage(new Date(), themeBg)), 60_000);
    return () => clearInterval(id);
  }, [themeBg]);

  return stage;
}
